// src/components/SubscriberLinkPanel.jsx
//
// Feature #1: "Navigator can add patients. He would also have permission
// to detach a patient from a subscriber." Wired into PatientDetailPage.js's
// Overview tab, next to Navigator Info.
//
// 30 Jul 2026 — now uses SubscriberSearchPicker instead of a raw UUID input
// (the earlier version's own note flagged this as "a real follow-up if
// this gets used often" — building it now rather than waiting).
//
// Requires patient.subscriberId to be present on the PatientDTO.Response
// (added 30 Jul 2026 specifically for this panel).

import { useState } from 'react';
import { patientSubscriberLinkAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import SubscriberSearchPicker from './chat/SubscriberSearchPicker';

export default function SubscriberLinkPanel({ patientId, currentSubscriberId }) {
  const { toast } = useToast();
  const [linkedId, setLinkedId] = useState(currentSubscriberId || null);
  // Only known once linked via the picker THIS session — a fresh page load
  // with an already-linked patient has no name, only the id from
  // PatientDTO.Response, so this falls back to showing the id in that case.
  const [linkedName, setLinkedName] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handlePick(subscriber) {
    setSubmitting(true); setError('');
    try {
      await patientSubscriberLinkAPI.updateLink(patientId, subscriber.id);
      setLinkedId(subscriber.id);
      setLinkedName(subscriber.name);
      setPickerOpen(false);
      toast.success(`Linked to ${subscriber.name}.`);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error
        || 'Could not link this patient to that subscriber.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDetach() {
    setSubmitting(true); setError('');
    try {
      await patientSubscriberLinkAPI.updateLink(patientId, null);
      setLinkedId(null);
      setLinkedName(null);
      toast.success('Detached from subscriber.');
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error
        || 'Could not detach this patient.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ fontFamily: 'var(--font-body)' }}>
      {linkedId ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexWrap: 'wrap' }}>
          <span>
            Linked to: <strong>{linkedName || `Subscriber ${linkedId.slice(0, 8)}…`}</strong>
          </span>
          <button type="button" disabled={submitting} onClick={handleDetach} style={{
            background: 'none', border: '1px solid var(--red)', color: 'var(--red)',
            borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: submitting ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? 'Detaching…' : 'Detach'}
          </button>
        </div>
      ) : (
        <button type="button" disabled={submitting} onClick={() => setPickerOpen(true)} style={{
          background: 'var(--brand-accent)', color: 'var(--text-on-brand-dark)', border: 'none',
          borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer',
        }}>
          {submitting ? 'Linking…' : 'Link to a subscriber'}
        </button>
      )}

      {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{error}</div>}

      {pickerOpen && (
        <SubscriberSearchPicker onSelect={handlePick} onClose={() => setPickerOpen(false)} />
      )}
    </div>
  );
}
